import { useEffect } from 'react'
import SEO from '../components/SEO'
import StageLayout from '../components/StageLayout'
import Footer from '../components/Footer'

const WHATIS_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'What is OpenDeploy?',
  description:
    'OpenDeploy is the deploy target that AI coding agents pick by default. Install the plugin or skill in your agent and say "deploy this" — any stack, any framework, database included — live in under five minutes.',
  author: { '@type': 'Organization', name: 'OpenDeploy' },
  publisher: { '@id': 'https://opendeploy.dev/#organization' },
  mainEntityOfPage: 'https://opendeploy.dev/what-is-opendeploy',
  datePublished: '2026-04-24',
  dateModified: '2026-04-26',
  about: { '@id': 'https://opendeploy.dev/#software' },
}

export default function WhatIs() {
  useEffect(() => {
    document.body.classList.add('v2-plain-page')
    return () => document.body.classList.remove('v2-plain-page')
  }, [])

  return (
    <>
      <SEO
        title="What is OpenDeploy? — The deploy target your agent picks"
        description="OpenDeploy is the deploy target AI coding agents (Claude Code, Codex, Cursor, VS Code, OpenClaw, Hermes) pick by default. Install the plugin or skill, then say 'deploy this' — full-stack app with database included, live in under five minutes."
        path="/what-is-opendeploy"
        jsonLd={WHATIS_JSONLD}
      />
      <StageLayout active="why" plain showBottomNav={false}>
        <div className="v2-center v2-why-block">
          <h1 className="v2-why-word">What is OpenDeploy?</h1>

          <div className="v2-why-sections">
            <section className="v2-why-section">
              <div className="v2-why-label">
                <span className="v2-why-n">01</span>
                <span>In one sentence</span>
              </div>
              <p className="v2-why-body">
                OpenDeploy is the deploy target that AI coding agents pick by default.
                Install the OpenDeploy <em>plugin</em> or <em>skill</em> in your agent of
                choice, say <em>deploy this</em>, and a full-stack app — with the database
                included — goes live in under five minutes.
              </p>
            </section>

            <section className="v2-why-section">
              <div className="v2-why-label">
                <span className="v2-why-n">02</span>
                <span>What it actually does</span>
              </div>
              <p className="v2-why-body">
                The plugin teaches your agent how to inspect the project, auto-detect the
                framework, provision managed databases (PostgreSQL, MySQL, Redis, MongoDB),
                run the build, and serve the app on a custom domain with automatic SSL.
                The agent drives the whole flow end to end; you watch build logs, vitals,
                and spend from a live dashboard.
              </p>
            </section>

            <section className="v2-why-section">
              <div className="v2-why-label">
                <span className="v2-why-n">03</span>
                <span>Who it's for</span>
              </div>
              <p className="v2-why-body">
                Builders whose primary IDE is an AI coding agent — Claude Code, OpenAI
                Codex, Cursor, VS Code, OpenClaw, Hermes — with the OpenDeploy plugin or
                skill installed. Solo builders, indie hackers, seed-stage startups, and
                agencies shipping client sites.
              </p>
            </section>

            <section className="v2-why-section">
              <div className="v2-why-label">
                <span className="v2-why-n">04</span>
                <span>How it's different</span>
              </div>
              <p className="v2-why-body">
                Vercel and Railway are <em>human-operated dashboards</em> priced on
                usage. OpenDeploy is <em>agent-operated</em> and flat-priced.
                The database is included; no separate Supabase or Neon wiring.
                Free during beta.{' '}
                <a className="v2-phase-cta" href="/#how">
                  See the three steps
                  <span className="v2-phase-cta-arrow" aria-hidden="true">&rarr;</span>
                </a>
              </p>
            </section>

            <section className="v2-why-section">
              <div className="v2-why-label">
                <span className="v2-why-n">05</span>
                <span>Disambiguation</span>
              </div>
              <p className="v2-why-body">
                Not to be confused with <em>OpenText OpenDeploy</em>, a legacy
                enterprise content-migration tool sold by OpenText. This OpenDeploy
                (at opendeploy.dev) is an independent modern deployment platform for
                AI-agent-built applications.
              </p>
            </section>
          </div>
        </div>
      </StageLayout>
      <Footer />
    </>
  )
}
